import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Table,
  Header,
  Button,
  ButtonDropdown,
  TextFilter,
  Pagination,
  SpaceBetween,
  Box,
  StatusIndicator,
  ContentLayout,
  Grid,
  SplitPanel,
} from '@cloudscape-design/components';
import {
  defaultVisibleColumns,
  getQuestionsTableDefinition,
  exportQuestionsConfig,
} from './questionConfig';

import TablePreferences, {
  ColumnDefinition,
  TablePreferencesRef,
} from 'src/components/TablePreferences';
import { useQuestion } from 'src/store/question/useQuestion';
import { ExportButton } from 'src/components/ExportButton';
import { PaginationConfig } from 'src/contstant/PaginationConfig';
import useUserProfile from 'src/hooks/useUserProfile';
import AppPagination, { AppPaginationRef } from 'src/components/AppPagination';
import routePaths from 'src/contstant/routePaths';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Question, SearchQuestionRequest } from 'src/types/helios/Question';
import QuestionFilterView from 'src/components/question/QuestionFilterView';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { useUsers } from 'src/store/users/useUsers';
import { DEFAULT_LANGUAGE, PAGE_SIZE_OPTIONS } from 'src/contstant/commonLabels';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import { ApiRequestStatus } from 'src/types/ApiRequestStatus';
import { LanguageDropdown } from 'src/components/LanguageDropdown';
import { openQuestionEditPage } from 'src/helper/questionHelper';
import { countActiveFilters } from 'src/utils/filterPersistence';
import { parseQuestionIds } from 'src/utils/questionUtils';
import { getPageFilterKey } from 'src/contstant/storageKeys';
import { usePageFilters } from 'src/hooks/usePageFilters';
import { SharedModeBanner } from 'src/components/SharedModeBanner';
import { BusinessUnitMismatchHandler } from 'src/components/BusinessUnitMismatchHandler';
import { ShareFiltersButton } from 'src/components/ShareFiltersButton';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import { useFeatureFlags } from 'src/context/FeatureFlagsContext';

const QuestionPage = () => {
  const { checkUserPermissions, userPermission, currentUser } = useUsers();
  const [urlSearchParams] = useSearchParams();

  const useRefPagiantion = useRef<AppPaginationRef>(null);
  const tablePreferencesRef = useRef<TablePreferencesRef>(null);

  const navigate = useNavigate();
  const {
    searchQuestionApiStatus,
    searchQuestions,
    questionsList,
    clearQuestionAllApiStatus,
  } = useQuestion();
  const { setContent, setTitle, closeView, content, setPanelSize } = useSplitPanel();
  const { businessUnitList, loadBusinessUnits } = useBusinessUnit();
  const { enableAuditWhiz } = useFeatureFlags();
  const {
    getUserBusinessUnitId,
    getPersona,
    getUserId,
    getUserProgramId,
    getDivisions,
    getUserSelectedLanguage,
    selectUserLanguage,
    getPreferLanguageList,
  } = useUserProfile();

  // Get business unit ID for scoped filter persistence
  const businessUnitId = getUserBusinessUnitId();

  const [shareFilterStatus, setShareFilterStatus] = useState<ApiRequestStatus[]>([]); // Share filter status messages

  const initSearchParams = {
    businessUnitId,
    language: DEFAULT_LANGUAGE,
    questionIdsText: undefined,
  };

  // Stable reference to default filter generator
  const getDefaultFilters = useCallback((): SearchQuestionRequest => {
    const categoryParam = urlSearchParams.get('category');
    return {
      businessUnitId: getUserBusinessUnitId(),
      language: DEFAULT_LANGUAGE,
      questionIdsText: undefined,
      ...(categoryParam && { categories: [categoryParam] }),
    };
  }, [urlSearchParams, getUserBusinessUnitId]);

  // Use page filters hook with shared URL support
  const {
    filters: searchParams,
    setFilters: setSearchParams,
    clearFilters,
    resetTarget,
    isSharedMode,
    isBUMismatch,
  } = usePageFilters({
    storageKey: getPageFilterKey(businessUnitId, 'questions'),
    defaultFilters: getDefaultFilters(),
    excludeFields: [
      'businessUnitId',
      'maxResults',
      'nextToken',
      'language',
      'additionalLanguages',
      'applyTemplateExclusions',
      'questionIds', // Exclude array version - we use questionIdsText instead
    ],
    universalFields: ['divisionName'], // Questions only use division, not locations
    currentBusinessUnitId: businessUnitId,
  });

  // Count active filters using generic utility
  const activeFilterCount = useMemo(() => {
    return countActiveFilters(searchParams, [
      'businessUnitId',
      'maxResults',
      'nextToken',
      'language',
      'additionalLanguages',
      'applyTemplateExclusions',
    ]);
  }, [searchParams]);

  const allColumnDefinition = useMemo(
    () =>
      getQuestionsTableDefinition(navigate, getUserSelectedLanguage()).filter(
        (col) => col.id !== undefined,
      ),
    [navigate, getUserSelectedLanguage()],
  );

  // Table preferences state
  const [tablePrefs, setTablePrefs] = useState<{
    visibleColumns: any[];
    paginatedItems: Question[];
    currentPage: number;
    pageSize: number;
  }>({
    visibleColumns: [],
    paginatedItems: [],
    currentPage: 1,
    pageSize: 0,
  });

  // Handler for table preferences changes
  const handlePreferencesChange = (data: {
    visibleColumns: any[];
    paginatedItems: any[];
    currentPage: number;
    pageSize: number;
  }) => {
    // useRefPagiantion?.current?.clearTokens()
    setTablePrefs((prev) => ({
      ...prev,
      visibleColumns: data.visibleColumns,
      currentPage: data.currentPage,
      pageSize: data.pageSize as PaginationConfig,
      paginatedItems: data.paginatedItems as typeof prev.paginatedItems,
    }));
  };

  const isEditQuestion = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.QUESTION.EDIT_QUESTION,
      userPermission?.permissions,
    );
  }, [userPermission?.permissions]);

  useEffect(() => {
    // Don't load data if there's a BU mismatch
    if (isBUMismatch) {
      return;
    }
    searchData(undefined, tablePrefs.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId, searchParams, tablePrefs.pageSize, isBUMismatch]);

  useEffect(() => {
    checkUserPermissions({
      userId: getUserId(),
      programId: getUserProgramId(),
      businessUnit: getUserBusinessUnitId(),
      permissions: [permissionAccessConfig.QUESTION.EDIT_QUESTION],
      divisions: getDivisions(),
    });
    loadBusinessUnits({
      programId: getUserProgramId(),
      isActive: true,
      isDeleted: false,
    });
    return () => {
      closeView();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  //when changing the language from dropdown , it refresh the content
  const selectedLanguage = getUserSelectedLanguage();
  useEffect(() => {
    tablePreferencesRef.current?.refreshColumns();
  }, [selectedLanguage]);

  const searchData = (nextToken?: string, maxResults?: number) => {
    if (maxResults === 0) {
      return;
    }
    if (!nextToken) {
      useRefPagiantion?.current?.clearTokens();
    }

    // Convert questionIdsText to questionIds array for API
    const { questionIdsText, ...restParams } = searchParams;
    const questionIds = parseQuestionIds(questionIdsText);

    searchQuestions({
      ...restParams,
      questionIds,
      maxResults: maxResults,
      nextToken: nextToken,
      businessUnitId: getUserBusinessUnitId(),
      language: DEFAULT_LANGUAGE,
      additionalLanguages: getPreferLanguageList(),
      applyTemplateExclusions: true,
    });
  };

  const openFilterView = () => {
    setPanelSize(window.innerWidth * 0.2);
    setTitle('Filter');
    setContent(
      <QuestionFilterView
        defaultSearchParams={searchParams}
        resetSearchParams={resetTarget}
        onFilterChange={function (filters: SearchQuestionRequest): void {
          setSearchParams(filters);
        }}
        onClearAll={clearFilters}
      />,
    );
  };

  // Update split panel content when searchParams changes (for cross-tab sync)
  useEffect(() => {
    if (
      content &&
      typeof content === 'object' &&
      'type' in content &&
      content.type === QuestionFilterView
    ) {
      setContent(
        <QuestionFilterView
          defaultSearchParams={searchParams}
          resetSearchParams={resetTarget}
          onFilterChange={function (filters: SearchQuestionRequest): void {
            setSearchParams(filters);
          }}
          onClearAll={clearFilters}
        />,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const tableHeader = (
    <Header
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          {/* Refresh data button */}
          <Button
            data-testid="refresh-button"
            iconName="refresh"
            onClick={() => searchData(undefined, tablePrefs.pageSize)}
          />
          <LanguageDropdown
            selectedLanguage={getUserSelectedLanguage()}
            defaultLanguage={DEFAULT_LANGUAGE}
            filterLanguges={getPreferLanguageList()}
            onLanguageChange={(value) => {
              selectUserLanguage(value);
            }}
          />
          <ExportButton
            data={questionsList}
            config={exportQuestionsConfig}
            onError={(error) => {
              alert(error);
            }}
          />

          {isEditQuestion() && !enableAuditWhiz && (
            <Button
              onClick={() => {
                openQuestionEditPage(navigate, false, undefined);
              }}
              variant="normal"
              data-testid="add-user-button"
            >
              Create Question
            </Button>
          )}

          {isEditQuestion() && enableAuditWhiz && (
            <ButtonDropdown
              variant="primary"
              data-testid="create-question-dropdown"
              mainAction={{
                text: 'Create Question',
                iconName: 'add-plus',
                onClick: () => openQuestionEditPage(navigate, false, undefined),
              }}
              items={[
                {
                  id: 'generate-with-astra',
                  text: 'Generate with Astra',
                  iconName: 'gen-ai',
                },
                {
                  id: 'create-manually',
                  text: 'Create manually',
                  iconName: 'add-plus',
                },
              ]}
              onItemClick={({ detail }) => {
                if (detail.id === 'create-manually') {
                  openQuestionEditPage(navigate, false, undefined);
                }
                if (detail.id === 'generate-with-astra') {
                  navigate('/question/generate');
                }
              }}
            />
          )}

          {!isBUMismatch && (
            <ShareFiltersButton
              data-testid="share-filters-button"
              filters={searchParams}
              businessUnitId={businessUnitId}
              onStatusChange={setShareFilterStatus}
            />
          )}
          {!content && !isBUMismatch && (
            <Button
              data-testid="filter-button"
              iconName="filter"
              onClick={() => {
                openFilterView();
              }}
            >
              {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
            </Button>
          )}
        </SpaceBetween>
      }
    >
      Questions
    </Header>
  );

  return (
    <ContentLayout>
      {/* Business Unit Mismatch Handler - automatically handles BU mismatch scenarios */}
      <BusinessUnitMismatchHandler
        isSharedMode={isSharedMode}
        isBUMismatch={isBUMismatch}
        currentBusinessUnitId={businessUnitId}
        requiredBusinessUnitId={searchParams?.businessUnitId}
        returnPath="/questions"
      />

      <Box margin={{ top: 's' }}>
        <ApiStatusFlashBar
          apiStatus={[...shareFilterStatus, searchQuestionApiStatus]}
          clearMessages={() => {
            clearQuestionAllApiStatus();
            setShareFilterStatus([]);
          }}
        />
        <SharedModeBanner isSharedMode={isSharedMode} isBUMismatch={isBUMismatch} />
        <Table
          header={tableHeader}
          columnDefinitions={tablePrefs.visibleColumns}
          loading={searchQuestionApiStatus.isLoading}
          loadingText="Loading questions"
          empty={
            <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <StatusIndicator type="warning">No questions.</StatusIndicator>
              </SpaceBetween>
            </Box>
          }
          pagination={
            <AppPagination
              ref={useRefPagiantion}
              nextToken={searchQuestionApiStatus.nextToken}
              onChange={(nextToken?: string) => {
                searchData(nextToken, tablePrefs.pageSize);
              }}
            />
          }
          items={questionsList}
          preferences={
            <TablePreferences
              tableId="questions-table"
              ref={tablePreferencesRef}
              defaultVisibleColumns={defaultVisibleColumns}
              defaultPageSize={PaginationConfig.QUESTIONS_MAX_RESULT}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              allColumnDefinitions={allColumnDefinition as any}
              allItems={questionsList || []}
              onPreferencesChange={handlePreferencesChange}
            />
          }
          filter={
            <SpaceBetween size="m" direction="horizontal">
              <TextFilter
                filteringText={searchParams.keyword || ''}
                filteringPlaceholder="Search Questions"
                filteringAriaLabel="Search Questions"
                onChange={({ detail }) => {
                  setSearchParams((prev) => ({
                    ...prev,
                    keyword:
                      detail.filteringText === '' ? undefined : detail.filteringText,
                  }));
                }}
              />
            </SpaceBetween>
          }
          stripedRows
          stickyHeader
        />
      </Box>
    </ContentLayout>
  );
};

export default QuestionPage;
